import { Overlay } from '@angular/cdk/overlay';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { Subject, debounceTime, filter, map, takeUntil } from 'rxjs';

@Component({
    selector     : 'search',
    templateUrl  : './search.component.html',
    encapsulation: ViewEncapsulation.None,
    exportAs     : 'fuseSearch',
    animations   : fuseAnimations,
    standalone   : true,
    imports      : [NgIf, MatButtonModule, MatIconModule, FormsModule, MatAutocompleteModule, ReactiveFormsModule, MatOptionModule, NgFor, RouterLink, NgTemplateOutlet, MatFormFieldModule, MatInputModule, NgClass],
    providers    : [
        {
            provide   : MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () =>
            {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.block();
            },
        },
    ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy
{
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    //@Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened: boolean = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();

    show: boolean = false;

    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _router: Router,
        private _elementRef: ElementRef,
        private _httpClient: HttpClient,
        private _renderer2: Renderer2,
    )
    {
        this.setContext();

        _router.events
            .pipe (
                filter((event) => event instanceof NavigationEnd),
                map((event: NavigationEnd) => this.setContext())
            )
            .subscribe(() => {

            });       
    }

    @Input()
    public searchHandler: any = () => { };
    __isSuspendSearchHandler: boolean = false;

    @HostBinding('class') get classList(): any
    {
        return {
            'search-appearance-bar'  : this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened'          : this.opened,
        };
    }

    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef)
    {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if ( value )
        {
            // Give Angular time to complete the change detection cycle
            setTimeout(() =>
            {
                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete)
    {
        this._matAutocomplete = value;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        // Appearance
        if ('appearance' in changes)
        {
            // search after changing the appearance
            this.close();
        }
    }

    ngOnInit(): void
    {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                takeUntil(this._unsubscribeAll),
                map((value) =>
                {
                    if ( !value || value.length < this.minLength )
                    {
                        this.resultSets = null;
                    }

                    return value;
                }),
                // Filter out undefined/null/false statements and also
                // filter out the values that are smaller than minLength
                //filter(value => value && value.length >= this.minLength),
            )
            .subscribe((value) =>
            {
                if (!this.__isSuspendSearchHandler) {
                    this.searchHandler()(value);
                }

                if (this.__isSuspendSearchHandler) {
                    this.__isSuspendSearchHandler = false;
                }
            });
    }

    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
    
    onKeydown(event: KeyboardEvent): void
    {
        // Escape
        if ( event.code === 'Escape' )
        {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if ( this.appearance === 'bar' && !this._matAutocomplete.isOpen )
            {
                this.close();
            }
        }
    }

    open(): void
    {
        // Return if it's already opened
        if ( this.opened )
        {
            return;
        }

        // Open the search
        this.opened = true;
    }
    close(): void
    {
        // Return if it's already closed
        if ( !this.opened )
        {
            return;
        }

        // Clear the search input
        this.searchControl.setValue(null);

        // Close the search
        this.opened = false;
    }

    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    setContext() {
        switch (true) {         
            case window.location.pathname.indexOf(`/backup-new-booking/choose`) > -1:                
            case window.location.pathname.indexOf(`/new-booking/choose`) > -1:
                this.show = true;
                break;
            case window.location.pathname.indexOf(`/calendar`) > -1:
                this.show = false;
                break;
            case window.location.pathname.indexOf(`/my-week`) > -1:
                this.show = false;
                break;
            case window.location.pathname.indexOf(`/my-team`) > -1:
                this.show = false;
                break;
            case window.location.pathname.indexOf(`/map`) > -1:
                this.show = false;
                break;
            case window.location.pathname.indexOf(`/people`) > -1:
                this.__isSuspendSearchHandler = true;
                this.searchControl.setValue(null);
                this.opened = false;
                this.show = true;
                break;
            default:
                this.show = false;
        }
    }
}
